/** P1 운영 관제 센터 상태 카드. */
import { OperationsOverview, TelegramChannel } from '../../services/operationsApi';

type Tone = 'ok' | 'warn' | 'bad' | 'muted' | 'pending';

interface CardProps {
  title: string;
  value: string;
  sub?: string;
  tone: Tone;
}

function StatusCard({ title, value, sub, tone }: CardProps) {
  return (
    <article className={`op-status-card op-tone-${tone}`}>
      <span className="op-status-card-title">{title}</span>
      <strong className="op-status-card-value">{value}</strong>
      {sub && <span className="op-status-card-sub">{sub}</span>}
    </article>
  );
}

function marketCard(market: OperationsOverview['market']): CardProps {
  if (market.status === 'open') return { title: 'Market', value: 'Open', tone: 'ok' };
  if (market.status === 'closed_holiday') {
    return { title: 'Market', value: 'Closed', sub: 'Holiday', tone: 'muted' };
  }
  if (market.status === 'closed_regular') {
    return { title: 'Market', value: 'Closed', sub: 'Regular hours', tone: 'muted' };
  }
  return { title: 'Market', value: 'Unknown', sub: market.source, tone: 'pending' };
}

function schedulerCard(s: OperationsOverview['scheduler']): CardProps {
  const last = s.last_execution_at || s.latest_execution_log_at;
  if (!last) return { title: 'Scheduler', value: 'No data', tone: 'pending' };
  return {
    title: 'Scheduler',
    value: 'Active',
    sub: `last log: ${shortIso(last)}`,
    tone: 'ok',
  };
}

function brokerSyncCard(b: OperationsOverview['broker_sync']): CardProps {
  if (b.unsynced_count > 0) {
    return {
      title: 'Broker Sync',
      value: `${b.long_positions_count} long`,
      sub: `${b.unsynced_count} unsynced`,
      tone: 'warn',
    };
  }
  return {
    title: 'Broker Sync',
    value: `${b.long_positions_count} long`,
    sub: '0 unsynced',
    tone: 'ok',
  };
}

function signalHealthCard(h: OperationsOverview['strategy_signal_health']): CardProps {
  if (!h || h.status === 'no_data') {
    return { title: 'Signal API', value: 'No data', sub: 'recent 60m', tone: 'pending' };
  }
  if (h.status === 'ok') {
    return { title: 'Signal API', value: 'OK', sub: `${h.total} checks`, tone: 'ok' };
  }
  const symbols = h.latest_failure_symbols.join(', ') || 'unknown';
  return {
    title: 'Signal API',
    value: `${h.failure_count} fail`,
    sub: symbols,
    tone: h.status === 'bad' ? 'bad' : 'warn',
  };
}

function backupCard(backup: OperationsOverview['backup']): CardProps {
  if (backup.status === 'ok') {
    const hours = backup.age_seconds != null ? Math.floor(backup.age_seconds / 3600) : null;
    return { title: 'Backup', value: 'OK', sub: hours != null ? `${hours}h ago` : 'recent', tone: 'ok' };
  }
  if (backup.status === 'not_configured') {
    return { title: 'Backup', value: 'Not configured', tone: 'pending' };
  }
  if (backup.status === 'stale') {
    const hours = backup.age_seconds != null ? Math.floor(backup.age_seconds / 3600) : null;
    return { title: 'Backup', value: 'Stale', sub: hours != null ? `${hours}h ago` : backup.message, tone: 'warn' };
  }
  return { title: 'Backup', value: backup.status, sub: backup.message, tone: 'bad' };
}

function orderGuardCard(t: OperationsOverview['trading']): CardProps {
  if (t.paper_trading || !t.live_trading_enabled) {
    return { title: 'Order Guard', value: t.order_guard, sub: t.paper_trading ? 'Orders locked' : 'Live locked', tone: 'ok' };
  }
  return { title: 'Order Guard', value: t.order_guard, sub: 'US live_pilot ON', tone: 'warn' };
}

function tradingModeCard(t: OperationsOverview['trading']): CardProps {
  const us = t.toss_live_market === 'US' && t.toss_us_live_enabled;
  const kr = t.toss_live_market === 'KR' && t.toss_kr_live_enabled;
  const sub = t.paper_trading ? 'Orders locked' : us ? 'Toss US live_pilot' : kr ? 'Toss KR live_pilot' : 'Live route locked';
  return { title: 'Trading Mode', value: t.trading_mode, sub, tone: t.paper_trading ? 'ok' : 'warn' };
}
function telegramCard(c: TelegramChannel, label: string): CardProps {
  if (c.status === 'configured') return { title: label, value: 'Configured', tone: 'ok' };
  if (c.status === 'not_implemented')
    return { title: label, value: 'Not implemented', sub: 'P8 영역', tone: 'pending' };
  return { title: label, value: 'Not configured', tone: 'warn' };
}

export default function OperationsStatusCards({ data }: { data: OperationsOverview }) {
  const trade = data.telegram.channels.find((c) => c.channel === 'trade_alerts');
  const daily = data.telegram.channels.find((c) => c.channel === 'daily_report');

  const cards: CardProps[] = [
    tradingModeCard(data.trading),
    orderGuardCard(data.trading),
    marketCard(data.market),
    schedulerCard(data.scheduler),
    signalHealthCard(data.strategy_signal_health),
    brokerSyncCard(data.broker_sync),
    backupCard(data.backup),
    trade ? telegramCard(trade, 'Trade Alerts') : { title: 'Trade Alerts', value: '-', tone: 'muted' },
    daily ? telegramCard(daily, 'Daily Report') : { title: 'Daily Report', value: '-', tone: 'muted' },
  ];

  return (
    <section className="op-status-cards-grid">
      {cards.map((c) => (
        <StatusCard key={c.title} {...c} />
      ))}
    </section>
  );
}

function shortIso(iso: string): string {
  try {
    const date = new Date(iso);
    return date.toLocaleString('ko-KR', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
